import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { useUserQuiz, useUserQuizType } from '@lib/core/quizzes/hooks';
import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import { isApplicationKiosk } from '@lib/core/service/consts';
import { useApp } from '@lib/core/service/hooks';
import { useTechnical } from '@lib/core/service/hooks/useTechnical';
import { actionGetClientIp } from '@lib/core/service/slices/technical/clientIp';
import { SERVICE_TERMS_CATEGORIES } from '@lib/core/serviceTerms/consts';
import { useServiceTerms } from '@lib/core/serviceTerms/hooks/useServiceTerms';
import { useDietaryPreferences, useUser } from '@lib/core/users/hooks';
import {
  selectWishlistProductInstanceIds,
  selectWishlistProductListLoading,
} from '@lib/core/users/selectors/productList';
import { selectProductRateData } from '@lib/core/users/selectors/productRate';
import initTrackers from '@lib/tools/dat';
import { useLogging } from '@lib/tools/dat/hooks';
import MixpanelTracker, { MixpanelExtras, useDataChangedEffect } from '@lib/tools/dat/mixpanel';
import { PRODUCT_CATEGORY_NONE } from '@lib/tools/shared/helpers/consts';
import { useEffectSkipFirst } from '@lib/tools/views/hooks';
import { useTypedDispatch } from '@lib/tools/views/hooks/useTypedDispatch';

/**
 * This HOC can be used to set listeners on required keys in the store.
 */
const DatProvider = ({ children }) => {
  const dispatch = useTypedDispatch();
  const hasSentEntryPointRef = useRef(false);

  const {
    userCharactersIds,
    userLastName,
    userFirstName,
    userEmail: email,
    userGroups,
    userId,
    profileCountry,
    profileGenderSlug,
    profileBirthYear,
    mixpanelUserDidIdentify: mixPanelDidIdentify,
    isUserAuthenticated,
    userBestMatchCharacterByProductCategory,
    profileId,
  } = useUser();

  const { productCategory } = useApp();
  const { retailerId } = useRetailer();
  const { serviceTermsByCategory } = useServiceTerms();
  const { [SERVICE_TERMS_CATEGORIES.profiling]: profiling } = serviceTermsByCategory;

  const { userDietaryPreferencePreferencesSlugs, userDietaryPreferenceTagsSlugs } = useDietaryPreferences();

  const { isClientIpBlocked, isClientIpLoading } = useTechnical();
  const { isLoggingEnabled, mixPanelState } = useLogging();

  const feedbackData = useSelector(selectProductRateData);

  const isWishlistProductListLoading = useSelector(selectWishlistProductListLoading);
  const wishlistProductInstanceIds = useSelector(selectWishlistProductInstanceIds);

  const { userQuizType, isUserQuizComplete, userQuizState } = useUserQuiz();
  const { isUserQuizTypeTaste } = useUserQuizType();

  const shouldTrack = useCallback(
    /**
     * Determines whether a tracking method should be invoked based on 3 factors:
     * - Mixpanel library availability
     * - User identification status
     * - Whether the method should trigger even if the user is not identified
     *
     * @param setSuperPropsIfAnonymous - Whether the tracking method should set super properties regardless of the
     * user's identification status.
     */
    (setSuperPropsIfAnonymous: boolean) => {
      return !!(mixPanelState && (mixPanelDidIdentify || setSuperPropsIfAnonymous));
    },
    [mixPanelState, mixPanelDidIdentify],
  );

  // Store the return value of shouldTrack in a state variable
  const [trackIfAnonymous, setTrackIfAnonymous] = useState(shouldTrack(true));
  const [trackIfIdentified, setTrackIfIdentified] = useState(shouldTrack(false));

  useEffect(() => {
    if (!mixPanelState && !isClientIpLoading) {
      dispatch(actionGetClientIp()).then(() => initTrackers());
    }
  }, [dispatch, isClientIpLoading, mixPanelState]);

  // Must run before any track() calls: a persisted opt-out from a prior session would
  // otherwise silently drop early events (e.g. Entry Point) while later ones succeed.
  useEffect(() => {
    if (mixPanelState) {
      MixpanelExtras.switchTrackingOnOff(isClientIpBlocked || isLoggingEnabled, userGroups);
    }
  }, [mixPanelState, isClientIpBlocked, userGroups, isLoggingEnabled]);

  useEffect(() => {
    // Handle mixpanel if user logs out from the app
    if (mixPanelState && !isUserAuthenticated) {
      MixpanelTracker.events.signOut();
    }
  }, [mixPanelState, isUserAuthenticated]);

  // Super props must run before identify: on kiosk / disable-user-persistence this resets Mixpanel.
  useEffect(() => {
    if (mixPanelState && retailerId && productCategory !== PRODUCT_CATEGORY_NONE && profileId) {
      MixpanelExtras.initializeSuperProperties();
    }
  }, [mixPanelState, retailerId, productCategory, profileId]);

  useEffect(() => {
    // Handle mixpanel identification of the user when they authenticate or
    // start a new session when already authenticated
    if (mixPanelState && isUserAuthenticated && userId) {
      MixpanelTracker.profile.handleUserIdentity(userId);
    }
  }, [mixPanelState, isUserAuthenticated, userId]);

  useEffect(() => {
    // Auth web users: wait for identify so Entry Point is not orphaned on the anonymous distinct_id.
    // Kiosk never identifies via handleUserIdentity; entryPoint is also disabled there.
    const shouldWaitForIdentify = isUserAuthenticated && !isApplicationKiosk;
    const isReadyForEntryPoint =
      mixPanelState &&
      retailerId &&
      productCategory !== PRODUCT_CATEGORY_NONE &&
      profileId &&
      (!shouldWaitForIdentify || mixPanelDidIdentify);

    if (!isReadyForEntryPoint || hasSentEntryPointRef.current) return;

    hasSentEntryPointRef.current = true;
    MixpanelTracker.events.entryPoint();
  }, [retailerId, productCategory, mixPanelState, profileId, isUserAuthenticated, mixPanelDidIdentify]);

  useEffectSkipFirst(() => {
    if (userQuizType) {
      MixpanelTracker.events.testStarted(
        userQuizState.data,
        isUserQuizTypeTaste ? userDietaryPreferencePreferencesSlugs : null,
      );
    }
  }, [userQuizType]);

  useEffectSkipFirst(() => {
    if (isUserQuizComplete) MixpanelTracker.events.testCompleted(userQuizState.data);
  }, [isUserQuizComplete]);

  useEffect(() => {
    // Update the state variables whenever shouldTrack's return value changes
    setTrackIfAnonymous(shouldTrack(true));
    setTrackIfIdentified(shouldTrack(false));
  }, [shouldTrack]);

  useEffect(() => {
    // Anonymous user made a choice for profiling option
    if (trackIfAnonymous && profiling) {
      MixpanelTracker.profile.updateAnonymousProfilingOption(profiling);
    }
  }, [trackIfAnonymous, profiling]);

  useEffect(() => {
    // User privacy settings have been updated
    if (trackIfIdentified) {
      MixpanelTracker.profile.updateUserPrivacyOptions(Object.values(serviceTermsByCategory));
    }
  }, [trackIfIdentified, serviceTermsByCategory]);

  useEffect(() => {
    // Auth user data have been updated
    if (trackIfIdentified) {
      MixpanelTracker.profile.updateUserPersonalInfo(email, userFirstName, userLastName);
    }
  }, [trackIfIdentified, email, userFirstName, userLastName]);

  useEffect(() => {
    // Profile data have been updated
    MixpanelTracker.profile.updateUserSociodemographicData(profileGenderSlug, profileBirthYear, profileCountry);
  }, [trackIfIdentified, profileGenderSlug, profileBirthYear, profileCountry]);

  useDeepCompareEffect(() => {
    // User characters have been updated
    if (trackIfAnonymous) {
      MixpanelTracker.profile.updateUserCharacters(userBestMatchCharacterByProductCategory);
    }
  }, [trackIfAnonymous, mixPanelDidIdentify, userCharactersIds]);

  useDeepCompareEffect(() => {
    // User preferences have been updated
    if (trackIfAnonymous || trackIfIdentified) {
      MixpanelTracker.profile.updateUserPreferences(
        userDietaryPreferencePreferencesSlugs,
        userDietaryPreferenceTagsSlugs,
      );
    }
  }, [trackIfAnonymous, trackIfIdentified, userDietaryPreferencePreferencesSlugs, userDietaryPreferenceTagsSlugs]);

  // User product wishlist has been updated
  useDataChangedEffect({
    data: wishlistProductInstanceIds,
    deps: [trackIfIdentified, wishlistProductInstanceIds, isWishlistProductListLoading],
    effect: () => MixpanelTracker.profile.updateUserProductWishlist(wishlistProductInstanceIds),
    trackCondition: trackIfIdentified && !isWishlistProductListLoading,
  });

  useDeepCompareEffect(() => {
    // User product feedback list has been updated
    if (trackIfIdentified) {
      MixpanelTracker.profile.updateProductRate(feedbackData);
    }
  }, [trackIfIdentified, feedbackData]);

  return children;
};

export default DatProvider;
